import { useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  errorMessage,
  formatDate,
  formatMoney,
  DetailRow,
} from "./assetsShared";
import "./Assets.css";

/* ================= CONSTANTS ================= */
const CATEGORIES = [
  "Operating System",
  "Office Suite",
  "CAD / Design",
  "3D & Rendering",
  "Adobe",
  "Antivirus",
  "Utility",
  "Other",
];

const CATEGORY_COLOR_CLASS = {
  "Operating System": "as-card-blue",
  "Office Suite": "as-card-orange",
  "CAD / Design": "as-card-purple",
  "3D & Rendering": "as-card-indigo",
  Adobe: "as-card-red",
  Antivirus: "as-card-green",
  Utility: "as-card-teal",
  Other: "as-card-brown",
};

const LICENSE_TYPES = ["Subscription", "Perpetual", "Free", "Trial"];

const EXPIRING_DAYS = 30;

const emptyForm = {
  name: "",
  version: "",
  category: "CAD / Design",
  vendorName: "",
  vendorContactNo: "",
  vendorEmail: "",
  licenseType: "Subscription",
  licenseKey: "",
  totalSeats: 1,
  invoiceNo: "",
  purchaseDate: "",
  expiryDate: "",
  purchaseValue: "",
  notes: "",
  assignedUsers: [],
};

const daysLeft = (expiryDate) =>
  expiryDate
    ? Math.ceil((new Date(expiryDate).getTime() - Date.now()) / 86400000)
    : null;

const expiryInfo = (item) => {
  const days = daysLeft(item.expiryDate);

  if (days === null) return { label: "No expiry", tone: "ok" };
  if (days < 0) return { label: `Expired ${Math.abs(days)} day(s) ago`, tone: "bad" };
  if (days <= EXPIRING_DAYS) return { label: `Expires in ${days} day(s)`, tone: "warn" };

  return { label: `Valid till ${formatDate(item.expiryDate)}`, tone: "ok" };
};

const maskKey = (key) =>
  key ? `${"•".repeat(Math.max(4, key.length - 4))}${key.slice(-4)}` : "-";

export default function SoftwareMaster() {
  const [software, setSoftware] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [showRetired, setShowRetired] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [revealed, setRevealed] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const detailRef = useRef(null);

  /* ================= LOAD DATA ================= */
  const loadData = async () => {
    try {
      setError("");

      const [softwareResponse, usersResponse] = await Promise.all([
        api.get("/software"),
        api.get("/users"),
      ]);

      setSoftware(softwareResponse.data || []);
      setUsers(usersResponse.data || []);
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to load software."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const activeSoftware = useMemo(
    () => software.filter((item) => item.status === "ACTIVE"),
    [software]
  );

  /* ================= CATEGORY CARDS (ONLY CATEGORIES THAT HAVE ITEMS) ================= */
  const categoryCards = useMemo(() => {
    // known categories first, then any other category that exists in data
    const inData = [...new Set(activeSoftware.map((item) => item.category))];
    const allCategories = [
      ...CATEGORIES,
      ...inData.filter((category) => category && !CATEGORIES.includes(category)),
    ];

    return allCategories
      .map((category) => {
        const items = activeSoftware.filter((item) => item.category === category);
        const seats = items.reduce((sum, item) => sum + Number(item.totalSeats || 0), 0);
        const used = items.reduce(
          (sum, item) => sum + (item.assignedUsers?.length || 0),
          0
        );

        return {
          category,
          total: items.length,
          seats,
          used,
          available: Math.max(0, seats - used),
          expiring: items.filter((item) => expiryInfo(item).tone === "warn").length,
          expired: items.filter((item) => expiryInfo(item).tone === "bad").length,
        };
      })
      .filter((card) => card.total > 0);
  }, [activeSoftware]);

  /* ================= CARD TOGGLE ================= */
  const handleCardClick = (category) => {
    setShowRetired(false);

    // same card again = close details
    if (activeCategory === category) {
      setActiveCategory(null);
      return;
    }

    setActiveCategory(category);

    setTimeout(() => {
      detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  /* ================= DETAIL LIST ================= */
  const detailItems = useMemo(() => {
    const value = search.trim().toLowerCase();

    return software
      .filter((item) => item.status === (showRetired ? "RETIRED" : "ACTIVE"))
      .filter((item) => !activeCategory || showRetired || item.category === activeCategory)
      .filter(
        (item) =>
          !value ||
          [item.name, item.vendorName, item.category, item.version]
            .map((text) => String(text || "").toLowerCase())
            .some((text) => text.includes(value))
      );
  }, [software, activeCategory, showRetired, search]);

  // details only when a card is open, or searching, or retired list is on
  const showDetails = Boolean(activeCategory || search.trim() || showRetired);

  /* ================= FORM ================= */
  const setField = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));

  const toggleUser = (userId) =>
    setForm((current) => {
      const selected = current.assignedUsers.includes(userId)
        ? current.assignedUsers.filter((id) => id !== userId)
        : [...current.assignedUsers, userId];

      return { ...current, assignedUsers: selected };
    });

  const openAdd = () => {
    setForm({ ...emptyForm, category: activeCategory || emptyForm.category });
    setIsEdit(false);
    setEditId(null);
    setError("");
    setShowAdd(true);
  };

  const openEdit = (item) => {
    setForm({
      name: item.name || "",
      version: item.version || "",
      category: item.category || "Other",
      vendorName: item.vendorName || "",
      vendorContactNo: item.vendorContactNo || "",
      vendorEmail: item.vendorEmail || "",
      licenseType: item.licenseType || "Subscription",
      licenseKey: item.licenseKey || "",
      totalSeats: item.totalSeats || 1,
      invoiceNo: item.invoiceNo || "",
      purchaseDate: item.purchaseDate?.slice(0, 10) || "",
      expiryDate: item.expiryDate?.slice(0, 10) || "",
      purchaseValue: item.purchaseValue ?? "",
      notes: item.notes || "",
      assignedUsers: (item.assignedUsers || []).map((user) => user._id),
    });

    setEditId(item._id);
    setIsEdit(true);
    setError("");
    setShowAdd(true);
  };

  const handleSave = async (event) => {
    event.preventDefault();

    const payload = {
      ...form,
      totalSeats: Number(form.totalSeats) || 1,
      purchaseValue: Number(form.purchaseValue) || 0,
      purchaseDate: form.purchaseDate || null,
      expiryDate: form.expiryDate || null,
    };

    try {
      setSaving(true);
      setError("");

      if (isEdit) {
        await api.put(`/software/${editId}`, payload);
      } else {
        await api.post("/software", payload);
      }

      setShowAdd(false);
      setIsEdit(false);
      setEditId(null);
      setForm(emptyForm);
      await loadData();
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to save software."));
    } finally {
      setSaving(false);
    }
  };

  const handleRetire = async (item) => {
    const note = window.prompt("Reason for retiring this software?");
    if (!note || !note.trim()) return;

    try {
      setError("");
      await api.post(`/software/${item._id}/retire`, { note: note.trim() });
      await loadData();
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to retire software."));
    }
  };

  const seatsFull = form.assignedUsers.length >= (Number(form.totalSeats) || 1);

  return (
    <div className="as-page">
      <div className="as-header">
        <h2>Software Master</h2>

        <div className="as-header-actions">
          <button type="button" className="as-btn as-btn-primary" onClick={openAdd}>
            + Add Software
          </button>

          <button
            type="button"
            className="as-btn as-btn-secondary"
            onClick={() => {
              setShowRetired((current) => !current);
              setActiveCategory(null);
            }}
          >
            {showRetired ? "← Active Software" : "🗑 Retired Software"}
          </button>
        </div>
      </div>

      {error && <div className="as-alert">{error}</div>}

      <input
        className="as-search"
        placeholder="🔍 Search software, vendor or category..."
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      {loading && <div className="as-empty">Loading...</div>}

      {!loading && !activeSoftware.length && !showRetired && !error && (
        <div className="as-empty">
          No software added yet. "+ Add Software" par click karke pehla software add karo.
        </div>
      )}

      {/* ================= CATEGORY CARDS ================= */}
      {categoryCards.length > 0 && (
        <div className="as-card-grid">
          {categoryCards.map((c) => (
            <div
              key={c.category}
              className={`as-card ${CATEGORY_COLOR_CLASS[c.category] || ""} ${
                activeCategory === c.category ? "active" : ""
              }`}
              onClick={() => handleCardClick(c.category)}
            >
              <div className="as-card-title">
                <h3>{c.category}</h3>
                <p>Software</p>
              </div>

              <div className="as-stats">
                <div className="as-stat used">
                  <span>Seats Used</span>
                  <strong>{c.used}</strong>
                </div>

                <div className="as-stat available">
                  <span>Available</span>
                  <strong>{c.available}</strong>
                </div>

                <div className="as-stat city">
                  <span>Total Seats</span>
                  <strong>{c.seats}</strong>
                </div>

                {c.expiring > 0 && (
                  <div className="as-stat warn">
                    <span>Expiring</span>
                    <strong>{c.expiring}</strong>
                  </div>
                )}

                {c.expired > 0 && (
                  <div className="as-stat bad">
                    <span>Expired</span>
                    <strong>{c.expired}</strong>
                  </div>
                )}
              </div>

              <div className="as-total">
                <span>Total Licenses</span>
                <strong>{c.total}</strong>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ================= DETAILS ================= */}
      {showDetails && (
        <div ref={detailRef} className="as-detail-wrapper">
          <h3 className="as-section-title">
            {showRetired
              ? "Retired Software"
              : activeCategory || "Search Results"}{" "}
            <small>({detailItems.length})</small>
          </h3>

          {!detailItems.length && <div className="as-empty">No software found.</div>}

          <div className="as-detail-grid">
            {detailItems.map((item) => {
              const info = expiryInfo(item);
              const used = item.assignedUsers?.length || 0;
              const percent = Math.min(
                100,
                Math.round((used / Math.max(1, item.totalSeats)) * 100)
              );
              const retired = item.status === "RETIRED";

              return (
                <div
                  key={item._id}
                  className={`as-detail-card ${retired ? "destroyed" : ""}`}
                >
                  <h4 className="as-detail-title">{item.name}</h4>

                  <p className="as-detail-subtitle">
                    {item.category}
                    {item.version ? ` · v${item.version}` : ""}
                  </p>

                  <span className={`as-badge as-badge-${info.tone}`}>{info.label}</span>

                  <DetailRow label="License Type" value={item.licenseType} />

                  <div className="as-detail-row">
                    <span>License Key</span>
                    <strong>
                      {revealed[item._id] ? item.licenseKey || "-" : maskKey(item.licenseKey)}{" "}
                      {item.licenseKey && (
                        <button
                          type="button"
                          className="as-link-btn"
                          onClick={() =>
                            setRevealed((current) => ({
                              ...current,
                              [item._id]: !current[item._id],
                            }))
                          }
                        >
                          {revealed[item._id] ? "Hide" : "Show"}
                        </button>
                      )}
                    </strong>
                  </div>

                  <DetailRow label="Vendor" value={item.vendorName} />
                  <DetailRow label="Vendor Contact" value={item.vendorContactNo} />
                  <DetailRow label="Vendor Email" value={item.vendorEmail} />
                  <DetailRow label="Invoice No" value={item.invoiceNo} />
                  <DetailRow label="Purchase Date" value={formatDate(item.purchaseDate)} />
                  <DetailRow label="Expiry Date" value={formatDate(item.expiryDate)} />
                  <DetailRow label="Purchase Value" value={formatMoney(item.purchaseValue)} />

                  <div className="as-seats">
                    <div className="as-seats-text">
                      <span>Seats</span>
                      <strong>
                        {used} / {item.totalSeats}
                      </strong>
                    </div>

                    <div className="as-progress">
                      <div style={{ width: `${percent}%` }} />
                    </div>
                  </div>

                  <div className="as-chips">
                    {(item.assignedUsers || []).map((user) => (
                      <span key={user._id} className="as-chip">
                        {user.name}
                      </span>
                    ))}

                    {!used && <span className="as-chip as-chip-muted">Not assigned</span>}
                  </div>

                  {item.notes && <p className="as-note">{item.notes}</p>}

                  {!retired && (
                    <div className="as-detail-actions">
                      <button type="button" onClick={() => openEdit(item)}>
                        ✏️ Edit
                      </button>

                      <button
                        type="button"
                        className="danger"
                        onClick={() => handleRetire(item)}
                      >
                        🗑 Retire
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ================= ADD / EDIT DRAWER ================= */}
      {showAdd && (
        <div className="as-drawer-overlay">
          <div className="as-drawer">
            <div className="as-drawer-header">
              <h3>{isEdit ? "Edit Software" : "Add Software"}</h3>

              <button
                type="button"
                className="as-drawer-close"
                onClick={() => setShowAdd(false)}
              >
                ✖
              </button>
            </div>

            <form className="as-form-grid" onSubmit={handleSave}>
              <label className="as-field">
                <span>Software Name *</span>
                <input
                  required
                  placeholder="e.g. AutoCAD"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>Version</span>
                <input
                  placeholder="e.g. 2025"
                  value={form.version}
                  onChange={(e) => setField("version", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>Category *</span>
                <select
                  required
                  value={form.category}
                  onChange={(e) => setField("category", e.target.value)}
                >
                  {CATEGORIES.map((category) => (
                    <option key={category}>{category}</option>
                  ))}
                </select>
              </label>

              <label className="as-field">
                <span>License Type</span>
                <select
                  value={form.licenseType}
                  onChange={(e) => setField("licenseType", e.target.value)}
                >
                  {LICENSE_TYPES.map((type) => (
                    <option key={type}>{type}</option>
                  ))}
                </select>
              </label>

              <label className="as-field">
                <span>License Key</span>
                <input
                  value={form.licenseKey}
                  onChange={(e) => setField("licenseKey", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>Total Seats *</span>
                <input
                  required
                  type="number"
                  min="1"
                  value={form.totalSeats}
                  onChange={(e) => setField("totalSeats", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>Vendor Name</span>
                <input
                  value={form.vendorName}
                  onChange={(e) => setField("vendorName", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>Vendor Contact</span>
                <input
                  value={form.vendorContactNo}
                  onChange={(e) => setField("vendorContactNo", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>Vendor Email</span>
                <input
                  type="email"
                  value={form.vendorEmail}
                  onChange={(e) => setField("vendorEmail", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>Invoice Number</span>
                <input
                  value={form.invoiceNo}
                  onChange={(e) => setField("invoiceNo", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>Purchase Date</span>
                <input
                  type="date"
                  value={form.purchaseDate}
                  onChange={(e) => setField("purchaseDate", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>Expiry Date</span>
                <input
                  type="date"
                  value={form.expiryDate}
                  onChange={(e) => setField("expiryDate", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>Purchase Value</span>
                <input
                  type="number"
                  min="0"
                  value={form.purchaseValue}
                  onChange={(e) => setField("purchaseValue", e.target.value)}
                />
              </label>

              <label className="as-field as-field-full">
                <span>Notes</span>
                <textarea
                  rows="2"
                  value={form.notes}
                  onChange={(e) => setField("notes", e.target.value)}
                />
              </label>

              <div className="as-field as-field-full">
                <span>
                  Assign To ({form.assignedUsers.length} / {Number(form.totalSeats) || 1}{" "}
                  seats)
                </span>

                <div className="as-user-list">
                  {users.map((user) => {
                    const checked = form.assignedUsers.includes(user._id);

                    return (
                      <label
                        key={user._id}
                        className={`as-user-item ${checked ? "on" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!checked && seatsFull}
                          onChange={() => toggleUser(user._id)}
                        />
                        <span>{user.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="as-drawer-footer">
                <button
                  type="submit"
                  className="as-btn as-btn-primary"
                  disabled={saving}
                >
                  {saving ? "Saving..." : isEdit ? "Update" : "Save"}
                </button>

                <button
                  type="button"
                  className="as-btn as-btn-secondary"
                  onClick={() => setShowAdd(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}